import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren } from 'react';

import { Box, Heading, Text, Link, Container, Image, AccessibleDescription, SEO } from '@lucid/components';
import type { HeadFC } from 'gatsby';

import type { Lucid } from 'types/types';
import error404 from 'assets/svgs/error-404.svg';

const NotFoundPage: FC<PropsWithChildren<Lucid.Context.ErrorPageProps>> = ({ pageContext }) => {
  const {
    shortCode,
    urlLangPrefix,
    localeId,
    pageType,
    nid,
    shouldHideSsr,
    status,
    title,
    message,
    metatags,
    product,
  } = pageContext;

  return (
    <Layout
      hasFooter={status === 404}
      hasNavbar={status === 404}
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={[]}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      <Container extraPadding={3}>
        <Box
          css={theme => ({
            textAlign: 'center',
            maxWidth: '580px',
            marginTop: theme.space.s32,
            marginBottom: theme.space.s32,
            marginLeft: 'auto',
            marginRight: 'auto',
            [theme.breakpoints.bp900.min]: {
              marginTop: theme.space.s104,
              marginBottom: theme.space.s104,
            },
          })}
        >
          <AccessibleDescription isForScreenReaderOnly as="h1" SRText={title} />
          <Heading as="h2" css={{ textAlign: 'center' }} variant="hero3XL700">
            Sorry...
          </Heading>
          <Text variant="paragraphM400">{message}</Text>
          {status === 404 ? (
            <Link displayAsButton to="/">
              Return to the homepage
            </Link>
          ) : status !== 451 ? (
            <>
              <Text variant="paragraphM400"> If this problem continues, please contact us.</Text>
              <Link displayAsButton to="https://help.lucid.co/hc/en-us/requests/new">
                Contact us
              </Link>
            </>
          ) : null}
          <Box
            css={theme => ({
              marginTop: theme.space.s40,
              marginBottom: theme.space.s40,
            })}
          >
            <Image
              imageAlt={message}
              imageSrc={
                status === 404
                  ? error404
                  : 'https://s3.amazonaws.com/cashy-static-assets/app/webroot/img/generic_error_image.svg'
              }
            />
          </Box>
        </Box>
      </Container>
    </Layout>
  );
};

export default NotFoundPage;

export const Head: HeadFC<Lucid.Gatsby.Config.Metadata, Lucid.Context.ErrorPageHeadContext> = ({
  location,
  pageContext,
}) => {
  const { title, product } = pageContext;
  const metatags = [
    {
      key: 'title',
      value: `${title} | Lucid Software`,
    },
  ];

  return <SEO location={location} metatags={metatags} product={product} />;
};
